import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import useSeo from '@/hooks/useSeo';
import { channels, evidenceChecklist, guideSteps, misconceptions } from '@/mocks/guide';

export default function Guide() {
  useSeo({
    title: '维权指南 - 处理流程与证据清单｜锐察社社会爆料',
    description:
      '遇到消费纠纷不知道从哪开始？这份维权指南整理了从固定证据到逐级反馈的完整流程、必备证据清单与常见误区，帮你有条理地处理问题。',
    keywords: '社会爆料维权,维权指南,消费纠纷,证据清单,社会爆料纠纷',
    path: '/guide',
    type: 'article',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: '消费纠纷处理流程',
        description: '从固定证据到逐级反馈的完整处理流程。',
        step: guideSteps.map((item, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          name: item.title,
          text: item.desc,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sjbxw.cn/' },
          { '@type': 'ListItem', position: 2, name: '维权指南', item: 'https://sjbxw.cn/guide' },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-300 bg-background-100">
        <div className="mx-auto max-w-[1200px] px-4 py-10 md:px-6 md:py-14">
          <nav aria-label="面包屑" className="font-label text-sm text-foreground-500">
            <ol className="flex items-center gap-1.5">
              <li><Link to="/" className="transition-colors hover:text-primary-700">首页</Link></li>
              <li aria-hidden="true">/</li>
              <li className="text-foreground-800">维权指南</li>
            </ol>
          </nav>
          <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-7">
              <p className="font-label text-xs uppercase tracking-[0.2em] text-primary-600">
                Practical Guide
              </p>
              <h1 className="mt-3 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-4xl">
                遇到纠纷，先做对这五步
              </h1>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
                很多争议之所以难推进，不是因为没有道理，而是因为证据不完整、沟通没留痕、渠道用错了顺序。这份指南把处理过程拆开，让你一步步照着做。
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="h-[220px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[260px]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20desk%20with%20a%20clipboard%20checklist%2C%20folders%20and%20a%20cup%20of%20tea%2C%20organized%20workspace%2C%20warm%20muted%20tones%2C%20editorial%20documentary%20photography%2C%20soft%20natural%20light%2C%20no%20text&width=1000&height=700&seq=rc-guide-desk&orientation=landscape"
                  alt="整理维权证据与流程清单的场景配图"
                  title="维权指南 - 证据清单"
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
        <section aria-labelledby="steps-title">
          <div className="border-b border-background-300 pb-4">
            <h2 id="steps-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              处理流程
            </h2>
            <p className="mt-2 text-sm text-foreground-600">按顺序推进，避免跳步导致关键证据缺失。</p>
          </div>
          <ol className="mt-8 space-y-6">
            {guideSteps.map((item) => (
              <li
                key={item.step}
                className="grid grid-cols-1 gap-4 rounded-lg border border-background-300 bg-background-50 p-6 md:grid-cols-[80px_1fr] md:gap-6"
              >
                <span className="font-heading text-4xl font-black text-primary-200">{item.step}</span>
                <div>
                  <h3 className="font-heading text-lg font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.tips.map((tip) => (
                      <span
                        key={tip}
                        className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900"
                      >
                        {tip}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>

        <section aria-labelledby="evidence-title" className="mt-14">
          <div className="border-b border-background-300 pb-4">
            <h2 id="evidence-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              证据清单
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              对照检查，能收集的先收集，暂时拿不到的也要记录获取方式。
            </p>
          </div>
          <ul className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {evidenceChecklist.map((item) => (
              <li
                key={item}
                className="flex items-start gap-3 rounded-md border border-background-300 bg-background-100 px-4 py-3 text-sm leading-relaxed text-foreground-700"
              >
                <i className="ri-checkbox-circle-line mt-0.5 text-secondary-600" aria-hidden="true"></i>
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section aria-labelledby="misconception-title" className="mt-14">
          <div className="border-b border-background-300 pb-4">
            <h2 id="misconception-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              常见误区
            </h2>
            <p className="mt-2 text-sm text-foreground-600">这几种做法看似合理，实际会让处理更被动。</p>
          </div>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {misconceptions.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-accent-200 bg-accent-50 p-5"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-200 text-accent-900">
                  <i className={`${item.icon} text-xl`} aria-hidden="true"></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section aria-labelledby="channel-title" className="mt-14">
          <div className="border-b border-background-300 pb-4">
            <h2 id="channel-title" className="font-heading text-2xl font-bold text-foreground-950 md:text-[28px]">
              渠道选择参考
            </h2>
            <p className="mt-2 text-sm text-foreground-600">不同渠道适合不同阶段，按情形选择更省力。</p>
          </div>
          <div className="mt-6 overflow-hidden rounded-lg border border-background-300">
            <div className="hidden grid-cols-4 gap-4 bg-foreground-950 px-5 py-3 font-label text-sm text-background-100 md:grid">
              <span>渠道</span>
              <span>适用场景</span>
              <span>大致节奏</span>
              <span>说明</span>
            </div>
            <ul className="divide-y divide-background-300">
              {channels.map((item) => (
                <li
                  key={item.name}
                  className="grid grid-cols-1 gap-2 bg-background-50 px-5 py-4 md:grid-cols-4 md:gap-4"
                >
                  <span className="font-heading text-base font-semibold text-foreground-950">{item.name}</span>
                  <span className="text-sm text-foreground-600">{item.scene}</span>
                  <span className="text-sm text-accent-800">{item.speed}</span>
                  <span className="text-sm text-foreground-600">{item.note}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-14 rounded-lg bg-foreground-950 px-6 py-10 md:px-10 md:py-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="max-w-xl">
              <h2 className="font-heading text-2xl font-bold text-background-50">
                需要针对具体情形的建议？
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-background-300">
                把事情的经过和已有的材料发给我们，我们会据此判断属于哪类议题，并给出可以参考的处理方向。
              </p>
            </div>
            <Link
              to="/about#contact"
              className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-accent-500 px-5 py-3 font-label text-sm text-foreground-950 transition-colors hover:bg-accent-400"
            >
              提交线索
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </section>

        <p className="mt-8 text-xs leading-relaxed text-foreground-500">
          说明：本页内容用于帮助理解处理流程，不构成法律意见。具体纠纷请结合个案情况，并咨询专业人士。
        </p>
      </div>
    </SiteLayout>
  );
}